<template>
  <div class="customer-dashboard-wrapper">
    <div class="background-image" :style="{ backgroundImage: `url(${customerPicture})` }"></div>

    <v-card class="main-card">
      <v-row no-gutters>
        <v-col cols="6">
          <div class="d-flex justify-start ma-4">
            <v-btn
              :loading="fetchingUserPoolData"
              :x-large="!$vuetify.breakpoint.mdAndUp"
              :medium="$vuetify.breakpoint.mdAndUp"
              :icon="!$vuetify.breakpoint.mdAndUp"
              class="rounded-lg neumorphic-button"
              @click="$emit('reload-customer')">
              <v-icon>mdi-reload</v-icon><span v-if="$vuetify.breakpoint.mdAndUp">Aktualisieren</span>
            </v-btn>
          </div></v-col
        >
        <v-col cols="6">
          <div class="d-flex justify-end ma-4">
            <v-btn
              :x-large="!$vuetify.breakpoint.mdAndUp"
              :medium="$vuetify.breakpoint.mdAndUp"
              :icon="!$vuetify.breakpoint.mdAndUp"
              class="rounded-lg neumorphic-button"
              @click="$emit('clear-customer')">
              <v-icon>mdi-account-search</v-icon>
              <span v-if="$vuetify.breakpoint.mdAndUp">Anderer Kunde</span>
            </v-btn>
          </div></v-col
        >
      </v-row>

      <div class="avatar-container">
        <v-avatar size="120" class="customer-avatar">
          <v-img :src="customerPicture" :lazy-src="'/img/icons/user_placeholder.png'" />
        </v-avatar>
      </div>

      <v-card-title class="justify-center pt-4 text-h5 font-weight-bold"> {{ customer.vorname }} {{ customer.nachname }} </v-card-title>

      <v-card-subtitle class="text-center pb-2">{{ customer.email }}</v-card-subtitle>
      <div class="d-flex justify-center">
        <v-btn medium class="mb-4 mt-2 neumorphic-button" @click="$router.push('/dashboard/user/search/' + customer._id)">
          <v-icon>mdi-account-edit</v-icon> zum Profil
        </v-btn>
      </div>

      <v-card-text>
        <transition name="view-swap" mode="out-in">
          <div v-if="viewMode === 'shortcuts'" key="shortcuts">
            <v-row>
              <v-col cols="12" md="6">
                <v-card class="interactive-card h-100" @click="showInteractionsDialog = true" :disabled="fetchingUserPoolData">
                  <v-card-text class="d-flex align-center">
                    <v-avatar color="blue lighten-4" size="60" class="mr-4">
                      <v-icon color="blue darken-1">mdi-face-agent</v-icon>
                    </v-avatar>
                    <div class="flex-grow-1">
                      <div class="card-action-title">Interaktionen</div>
                      <div v-if="fetchingUserPoolData" class="card-status-text grey--text">Lade Daten...</div>
                      <template v-else>
                        <div v-if="interactionInfo.total > 0" class="card-status-text">Letzte: {{ interactionInfo.timeAgo }}</div>
                        <div v-else class="card-status-text">Keine erfasst</div>
                      </template>
                    </div>
                    <v-chip v-if="!fetchingUserPoolData" :color="interactionInfo.status.color" small label>
                      {{ interactionInfo.status.text }}
                    </v-chip>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card class="interactive-card h-100" @click="viewMode = 'workoutManager'" :disabled="fetchingUserPoolData">
                  <v-card-text class="d-flex align-center">
                    <v-avatar color="orange lighten-4" size="60" class="mr-4">
                      <v-icon color="orange darken-1">mdi-dumbbell</v-icon>
                    </v-avatar>
                    <div>
                      <div class="card-action-title">Trainingspläne</div>
                      <div v-if="fetchingUserPoolData" class="card-status-text grey--text">Lade Daten...</div>
                      <template v-else>
                        <div v-if="workoutInfo.newDoc" class="card-status-text">
                          <v-icon small :color="getIconStateByDateForTP(workoutInfo.newDoc)" class="mr-1">mdi-check</v-icon>
                          Plan vom {{ workoutInfo.newDoc }}
                        </div>
                        <div v-else-if="workoutInfo.oldDoc" class="card-status-text">
                          <v-icon small :color="getIconStateByDateForTP(workoutInfo.oldDoc)" class="mr-1">mdi-alert-circle-outline</v-icon>
                          Plan vom {{ workoutInfo.oldDoc }}
                        </div>
                        <div v-else class="card-status-text">
                          <v-icon small color="error" class="mr-1">mdi-close</v-icon>
                          Kein Plan vorhanden
                        </div>
                      </template>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card class="interactive-card h-100" @click="showAnamneseDialog = true" :disabled="fetchingUserPoolData">
                  <v-card-text class="d-flex align-center">
                    <v-avatar color="teal lighten-4" size="60" class="mr-4">
                      <v-icon color="teal darken-1">mdi-heart-pulse</v-icon>
                    </v-avatar>
                    <div>
                      <div class="card-action-title">Anamnese</div>
                      <div v-if="fetchingUserPoolData" class="card-status-text grey--text">Lade Daten...</div>
                      <template v-else>
                        <div v-if="healthInfo.newDoc" class="card-status-text">
                          <v-icon small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                          {{ healthInfo.newDoc }}
                        </div>
                        <div v-else-if="healthInfo.oldDoc" class="card-status-text">
                          <v-icon small color="success" class="mr-1">mdi-check-circle-outline</v-icon>
                          {{ healthInfo.oldDoc }}
                        </div>
                        <div v-else class="card-status-text">
                          <v-icon small color="error" class="mr-1">mdi-close</v-icon>
                          Keine Daten erfasst
                        </div>
                      </template>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col cols="12" md="6">
                <v-card class="interactive-card h-100" @click="editMembership" :disabled="fetchingUserPoolData">
                  <v-card-text class="d-flex align-center">
                    <v-avatar color="purple lighten-4" size="60" class="mr-4">
                      <v-icon color="purple darken-1">mdi-card-account-details-outline</v-icon>
                    </v-avatar>
                    <div v-if="fetchingUserPoolData" class="flex-grow-1">
                      <div class="card-action-title">Mitgliedschaft</div>
                      <div class="card-status-text grey--text">Lade Daten...</div>
                    </div>
                    <div v-else-if="infos && infos.mainContract" class="flex-grow-1">
                      <div class="card-action-title">{{ infos.mainContract.NAME }}</div>
                      <div class="card-status-text">Gültig bis {{ contractEndDate }}</div>
                      <div v-if="infos.packages && infos.packages.length">
                        <v-chip v-for="pkg in infos.packages" :key="pkg.CONTRACTID" small outlined label class="mr-1 mb-1">
                          {{ pkg.NAME }}
                        </v-chip>
                      </div>
                    </div>
                    <div v-else class="flex-grow-1">
                      <div class="card-action-title">Mitgliedschaft</div>
                      <div class="card-status-text">Kein aktiver Vertrag</div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12" v-if="pendingUpgrades && pendingUpgrades.length > 0">
                <v-card class="info-card h-100" outlined style="border-color: var(--v-warning-base) !important; background-color: #fffaf3">
                  <v-card-title class="info-card-title warning--text">
                    <v-icon left color="warning">mdi-alert-circle-outline</v-icon> Offene Upgrades
                  </v-card-title>
                  <v-card-text class="pb-2">
                    <div
                      v-for="upgrade in pendingUpgrades"
                      :key="upgrade._id"
                      class="mb-4"
                      style="border-bottom: 1px solid #eee; padding-bottom: 12px">
                      <div class="font-weight-bold text-body-1 mb-2" style="color: #424242">Erstellt am: {{ upgrade.createdAt | formatDate }}</div>

                      <div
                        v-for="(pkg, i) in upgrade.packages"
                        :key="i"
                        class="d-flex justify-space-between text-body-2 mb-1 pl-2"
                        style="border-left: 2px solid var(--v-warning-base)">
                        <span> {{ pkg.amount || 1 }}x {{ pkg.packageName }} </span>
                        <span class="font-weight-medium"> CHF {{ calculatePackagePrice(pkg) }} </span>
                      </div>

                      <div class="d-flex justify-space-between mt-3 pt-2" style="border-top: 1px dashed #ccc">
                        <span class="font-weight-bold">Total:</span>
                        <span class="font-weight-bold warning--text text--darken-2">
                          {{ getUpgradeTotalString(upgrade) }}
                        </span>
                      </div>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>
              <v-col cols="12">
                <v-card class="info-card h-100" outlined>
                  <v-card-title class="info-card-title"><v-icon left>mdi-calendar-check</v-icon> Letzte GF Kurse</v-card-title>
                  <v-card-text class="pb-1">
                    <div v-if="fetchingUserPoolData" class="text-center grey--text py-4">Lade Kurshistorie...</div>
                    <div v-else-if="lastCourses.length > 0">
                      <div class="d-flex justify-space-between text-body-2 py-1" v-for="lastCourse of lastCourses" :key="lastCourse.ts">
                        <span>{{ lastCourse.course.name }}</span>
                        <span class="grey--text">{{ lastCourse.time }}</span>
                      </div>
                    </div>
                    <div v-else class="text-center grey--text py-4">Keine kürzlichen Kursteilnahmen.</div>
                  </v-card-text>
                </v-card>
              </v-col>
            </v-row>
          </div>

          <div v-else-if="viewMode === 'workoutManager'" key="manager">
            <v-btn text @click="viewMode = 'shortcuts'" class="mb-4 ml-n2">
              <v-icon left>mdi-arrow-left</v-icon>
              Zurück zum Cockpit
            </v-btn>

            <div>
              <h2 class="text-h6 font-weight-bold mb-3">Neuen Plan starten</h2>
              <v-row>
                <v-col cols="12" md="6">
                  <v-card class="action-card new-plan-card" @click="createNewPlan">
                    <div class="card-content">
                      <v-icon large class="mb-3" color="primary">mdi-star-four-points-outline</v-icon>
                      <span class="card-title">Neuer Plan </span>
                    </div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <v-divider class="my-6"></v-divider>

            <div>
              <h2 class="text-h6 font-weight-bold mb-4">Vorhandene Pläne</h2>

              <div v-if="loading" class="text-center py-8">
                <v-progress-circular indeterminate color="primary" size="50"></v-progress-circular>
              </div>

              <div v-else-if="!combinedPlans.length" class="text-center empty-state">
                <v-icon size="60" color="grey lighten-2">mdi-text-box-remove-outline</v-icon>
                <p class="mt-4 grey--text text--darken-1">Keine Pläne für diesen Kunden gefunden.</p>
              </div>

              <div v-else class="plan-list">
                <v-card v-for="(plan, index) in combinedPlans" :key="plan.id" class="plan-item-card mb-3" outlined>
                  <div
                    class="d-flex align-center"
                    :style="plan.type === 'new' ? 'cursor: pointer;' : ''"
                    @click="plan.type === 'new' ? toggleExpand(index) : null">
                    <div class="flex-grow-1">
                      <v-list-item-title class="font-weight-bold">{{ plan.title }}</v-list-item-title>
                      <v-list-item-subtitle> Erstellt am: {{ plan.createdDate | formatDate }} </v-list-item-subtitle>

                      <v-list-item-subtitle v-if="plan.type === 'old'"> Erstellt von: {{ plan.instructor }} </v-list-item-subtitle>

                      <v-list-item-subtitle v-else-if="plan.type === 'new' && plan.allContributors && plan.allContributors.length">
                        Beteiligte Trainer:
                        <span v-for="(trainer, tIndex) in plan.allContributors" :key="tIndex">
                          {{ trainer.vorname }} {{ trainer.nachname }}<span v-if="tIndex < plan.allContributors.length - 1">, </span>
                        </span>
                      </v-list-item-subtitle>
                    </div>
                    <v-chip small :color="plan.type === 'new' ? 'primary' : 'blue-grey'" dark class="ml-2 font-weight-bold">
                      {{ plan.type === "new" ? "Split-basiert" : "Klassisch" }}
                    </v-chip>
                    <v-chip small color="red" v-if="plan.deleted === true" dark class="ml-2 font-weight-bold"> Gelöscht </v-chip>
                    <v-icon v-if="plan.type === 'new'" class="ml-2">
                      {{ plan.isExpanded ? "mdi-chevron-up" : "mdi-chevron-down" }}
                    </v-icon>
                  </div>

                  <v-expand-transition>
                    <div v-if="plan.type === 'new' && plan.isExpanded" class="splits-container mt-3">
                      <v-divider class="mb-3"></v-divider>
                      <div v-for="(split, sIndex) in plan.splits" :key="sIndex" class="split-item">
                        <div class="split-header">
                          <span class="font-weight-bold text-body-2">{{ split.title }}</span>
                          <v-chip v-if="split.creatorInfo" outlined small color="primary" class="ml-3 split-creator-badge">
                            <v-icon left size="14">mdi-account-edit</v-icon> {{ split.creatorInfo.vorname }} {{ split.creatorInfo.nachname }}
                          </v-chip>
                          <v-spacer></v-spacer>
                          <span class="text-caption grey--text" v-if="split.exercises">{{ split.exercises.length }} Übungen</span>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>

                  <v-card-actions class="mt-1 pa-0">
                    <v-spacer></v-spacer>
                    <v-btn small text color="primary" v-if="plan.type === 'new'" @click="copyPlan(plan)">
                      <v-icon left small>mdi-content-copy</v-icon> Kopieren
                    </v-btn>
                    <v-btn small text color="primary" @click="editPlan(plan)"> <v-icon left small>mdi-pencil</v-icon> Bearbeiten </v-btn>
                    <v-btn small text color="primary" @click="printPlan(plan)"> <v-icon left small>mdi-printer</v-icon> Drucken </v-btn>
                    <v-btn small text color="red darken-1" v-if="plan.type === 'new' && !plan.deleted" @click="promptDeletePlan(plan)">
                      <v-icon left small>mdi-delete</v-icon> Löschen
                    </v-btn>
                    <v-btn v-else small text color="blue" @click="restorePlan(plan)"> Wiederherstellen </v-btn>
                  </v-card-actions>
                </v-card>
              </div>
            </div>
          </div>
        </transition>
      </v-card-text>
    </v-card>

    <v-dialog v-model="showInteractionsDialog" max-width="700px" scrollable>
      <v-card>
        <v-card-title class="text-h5">
          Interaktionsverlauf
          <v-spacer></v-spacer>
          <span class="font-weight-regular text-body-1">{{ customer.vorname }} {{ customer.nachname }}</span>
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text style="max-height: 60vh" class="pa-0">
          <div v-if="interactionInfo.total > 0" class="pa-4">
            <v-timeline align-top dense>
              <v-timeline-item
                v-for="interaction in interactionInfo.sorted"
                :key="interaction._id"
                :color="interaction.employee ? 'primary' : 'grey'"
                small>
                <div class="d-flex justify-space-between">
                  <div class="font-weight-bold">{{ interaction.interactions.reason }}</div>
                  <div class="text-caption grey--text">{{ interaction.interactions.date | formatDate }}</div>
                </div>
                <div class="text-body-2" v-if="interaction.employee">von {{ interaction.employee.vorname }} {{ interaction.employee.nachname }}</div>
                <div v-if="interaction.interactions.notes" class="text-body-2 mt-2 pa-3 grey lighten-4 rounded">
                  {{ interaction.interactions.notes }}
                </div>
              </v-timeline-item>
            </v-timeline>
          </div>
          <div v-else class="text-center grey--text py-10">
            <v-icon size="48" class="mb-2">mdi-comment-off-outline</v-icon>
            <p>Bisher keine Interaktionen erfasst.</p>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="showInteractionsDialog = false">Schliessen</v-btn>
          <v-btn color="primary" @click="triggerCreateInteraction">
            <v-icon left>mdi-plus</v-icon>
            Neue Interaktion
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showDeleteConfirmDialog" max-width="450" persistent content-class="delete-dialog-card">
      <v-card v-if="planToDelete" class="text-center pa-5">
        <div class="delete-dialog-bg"></div>
        <v-icon size="100" color="red darken-1" class="delete-icon mb-4">mdi-alert-octagon-outline</v-icon>
        <h2 class="font-weight-bold text-h5 white--text mb-3">Plan wirklich löschen?</h2>
        <p class="white--text text--lighten-1 mb-6">
          Bist du sicher, dass du den Plan <strong>"{{ planToDelete.title }}"</strong> dauerhaft löschen möchtest? Diese Aktion kann nicht rückgängig
          gemacht werden.
        </p>
        <v-row dense>
          <v-col cols="12">
            <v-btn large rounded block color="red darken-2" class="font-weight-bold" @click="confirmDeletePlan" :loading="loading">
              Endgültig löschen
            </v-btn>
          </v-col>
          <v-col cols="12">
            <v-btn large rounded block text @click="showDeleteConfirmDialog = false" class="font-weight-bold white--text">Abbrechen</v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showPrintSplitDialog" max-width="500">
      <v-card>
        <v-card-title class="font-weight-bold">Welchen Split möchtest du drucken?</v-card-title>
        <v-list>
          <v-list-item v-for="(split, index) in printableSplits" :key="index" @click="downloadSplit(index)" link>
            <v-list-item-icon><v-icon color="primary">mdi-download</v-icon></v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>{{ split.title }}</v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="showPrintSplitDialog = false">Schliessen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showAnamneseDialog" max-width="500">
      <v-card>
        <v-card-title class="font-weight-bold">Anamnesebögen</v-card-title>
        <v-list v-if="medicalHistoryInfo.length > 0">
          <v-list-item v-for="(mh, index) in medicalHistoryInfo" :key="index" @click="$emit('edit-anamnese', mh._id, false)" link>
            <v-list-item-icon>
              <v-icon color="primary">mdi-eye</v-icon>
            </v-list-item-icon>
            <v-list-item-content>
              <v-list-item-title>
                {{ $moment(mh.createdAt).format("DD.MM.YYYY HH:mm") }}
              </v-list-item-title>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon @click.stop="$emit('edit-anamnese', mh._id, true)">
                <v-icon color="secondary">mdi-pencil</v-icon>
              </v-btn>
            </v-list-item-action>
          </v-list-item>
        </v-list>
        <v-alert class="mx-5" type="info" v-else> Keine Anamnesebögen erstellt </v-alert>
        <v-card-actions>
          <v-btn text @click="showAnamneseDialog = false">Schliessen</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="$emit('create-anamnese', customer._id)">Erstellen</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <upgrade-contract
      v-if="customer && customer._id && showContractDialog"
      :selectedCustomer="customer._id"
      @hide="showContractDialog = false"
      @created="upgradeCreated"
      :dialog="showContractDialog"></upgrade-contract>
  </div>
</template>

<script>
import api from "@/api"
import { mapActions, mapGetters } from "vuex"
import upgradeContract from "@/views/dashboard/contract/components/upgradeContract.vue"

export default {
  name: "CustomerDashboard",
  components: { upgradeContract },
  props: {
    fetchingUserPoolData: { type: Boolean, default: false },
    customer: { type: Object, required: true },
  },
  filters: {
    formatDate(dateString) {
      if (!dateString) return ""
      const date = new Date(typeof dateString === "string" && dateString.includes("T") ? dateString : dateString)
      return date.toLocaleDateString("de-CH", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      })
    },
  },
  data() {
    return {
      pendingUpgrades: [],
      showAnamneseDialog: false,
      showContractDialog: false,
      viewMode: "shortcuts",
      loading: false,
      combinedPlans: [],
      showPrintSplitDialog: false,
      printableSplits: [],
      selectedPlanForPrint: null,
      showDeleteConfirmDialog: false,
      planToDelete: null,
      showInteractionsDialog: false,
      classicPlanDeadline: this.$moment("2025-10-05").endOf("day"),
    }
  },
  created() {
    if (!this.fetchingUserPoolData && !this.infos) {
      this.$emit("reload-customer")
    }
  },
  computed: {
    ...mapGetters("deviceStore", ["exerciseList", "fetchDeviceList"]),
    isClassicDeadlinePassed() {
      return this.$moment().isAfter(this.classicPlanDeadline)
    },
    showClassicDeprecationWarning() {
      return !this.isClassicDeadlinePassed
    },
    customerPicture() {
      if (this.customer?.bild) return "/img/profilbilder/" + this.customer.bild
      if (this.customer?.tac_image) return "data:image/jpeg;base64," + this.customer.tac_image
      return "/img/icons/user_placeholder.png"
    },
    infos() {
      return this.customer?.infos || null
    },
    contractEndDate() {
      if (!this.infos?.mainContract) return "N/A"
      return this.$moment(this.infos.mainContract.TERM_ENDDATE).format("DD.MM.YYYY")
    },
    lastCourses() {
      return this.infos?.courses?.length > 0 ? this.infos.courses : []
    },
    workoutInfo() {
      return this.getDocumentStatus(this.infos, ["workoutsV2"], ["workouts"])
    },
    medicalHistoryInfo() {
      return this.infos?.documents?.medicalHistory?.length > 0 ? this.infos.documents.medicalHistory : []
    },
    healthInfo() {
      return this.getDocumentStatus(this.infos, ["documents.medicalHistory"], ["documents.anamnese"])
    },
    interactionInfo() {
      const interactions = this.infos?.customerInteractions
      if (!interactions || interactions.length === 0) {
        return { total: 0, timeAgo: "Nie", status: { color: "error", text: "Keine" }, sorted: [] }
      }
      const sorted = [...interactions].sort(
        (a, b) => this.$moment(b.interactions?.date || b.createdAt).valueOf() - this.$moment(a.interactions?.date || a.createdAt).valueOf(),
      )
      const lastDate = this.$moment(sorted[0].interactions?.date || sorted[0].createdAt)
      const daysAgo = this.$moment().diff(lastDate, "days")
      let status
      if (daysAgo <= 14) status = { color: "success", text: "Aktuell" }
      else if (daysAgo <= 45) status = { color: "warning", text: "Handeln" }
      else status = { color: "error", text: "Überfällig" }
      return { total: interactions.length, timeAgo: lastDate.fromNow(), status, sorted }
    },
  },
  methods: {
    ...mapActions("workingoutStore", ["setEditInStore"]),
    ...mapActions("runWorkoutStore", ["setPlanForEditing", "setPlanForCloning"]),
    calculatePackagePrice(pkg) {
      // Nimmt den exakten Preis des Pakets aus dem JSON (z.B. 82.1917...) und rundet ihn auf 2 Nachkommastellen
      return parseFloat(pkg.price || 0).toFixed(2)
    },

    getUpgradeTotalString(upgrade) {
      // Nutzt direkt den fertigen String vom Backend (z.B. "CHF 82.20")
      if (upgrade.backendPriceString) {
        return upgrade.backendPriceString
      }
      // Fallback, falls der String aus irgendeinem Grund mal fehlen sollte
      return "CHF " + parseFloat(upgrade.frontendPrice || upgrade.backendPrice || 0).toFixed(2)
    },
    async loadUpgrades() {
      if (!this.customer?._id) return
      try {
        this.pendingUpgrades = await api.getCustomerUpgrades(this.customer._id)
        console.log(this.pendingUpgrades)
      } catch (error) {
        console.error("Fehler beim Laden der Upgrades:", error)
      }
    },

    openUpgrade(upgrade) {
      console.log("Upgrade öffnen:", upgrade)
      this.showContractDialog = true
    },
    toggleExpand(index) {
      this.$set(this.combinedPlans[index], "isExpanded", !this.combinedPlans[index].isExpanded)
    },

    upgradeCreated() {
      this.$toast.info("Update erstellt.")
    },
    editMembership() {
      if (this.infos && this.infos.mainContract && this.infos.mainContract.NAME) {
        this.showContractDialog = true
      } else {
        this.$router.push("/dashboard/contract")
      }
    },
    getIconStateByDateForTP(date) {
      const lastDate = this.$moment(date)
      const daysAgo = this.$moment().diff(lastDate, "days")
      let status
      if (daysAgo <= 84) status = "success"
      else if (daysAgo <= 126) status = "warning"
      else status = "error"
      return status
    },
    triggerCreateInteraction() {
      this.showInteractionsDialog = false
      setTimeout(() => {
        this.$emit("create-interaction", this.customer._id)
      }, 150)
    },
    getDocumentStatus(entry, newKeys, oldKeys) {
      if (!entry) return { newDoc: null, oldDoc: null }
      const findLatestDate = (keys) => {
        for (const key of keys) {
          const getValue = (obj, path) => path.split(".").reduce((o, i) => (o ? o[i] : undefined), obj)
          let docs = getValue(entry, key)
          if (docs && !Array.isArray(docs)) docs = [docs]
          if (Array.isArray(docs) && docs.length > 0) {
            const createMoment = (obj) => {
              if (obj.createdAt) return this.$moment(obj.createdAt)
              if (obj.date) return this.$moment.unix(obj.date)
              if (obj.updatedAt) return this.$moment(obj.updatedAt)
              if (obj.ts) return this.$moment.unix(obj.ts)
              if (obj.latest?.ts) return this.$moment.unix(obj.latest.ts)
              return null
            }
            const latest = [...docs].sort((a, b) => {
              const momentA = createMoment(a)
              const momentB = createMoment(b)
              return (momentB ? momentB.valueOf() : 0) - (momentA ? momentA.valueOf() : 0)
            })[0]
            const latestMoment = createMoment(latest)
            return latestMoment ? latestMoment.format("DD.MM.YYYY") : "Kein Datum"
          }
        }
        return null
      }
      return {
        newDoc: findLatestDate(newKeys),
        oldDoc: findLatestDate(oldKeys),
      }
    },
    promptDeletePlan(plan) {
      this.planToDelete = plan
      this.showDeleteConfirmDialog = true
    },
    async confirmDeletePlan() {
      if (!this.planToDelete) return
      this.loading = true
      try {
        await api.workoutApi.deleteWorkoutV2(this.planToDelete.id)
        this.$toast.success(`Plan "${this.planToDelete.title}" wurde gelöscht.`)
        await this.loadPlans()
      } catch (error) {
        console.error("Fehler beim Löschen des Trainingsplans:", error)
        this.$toast.error("Der Plan konnte nicht gelöscht werden.")
      } finally {
        this.showDeleteConfirmDialog = false
        this.planToDelete = null
        this.loading = false
      }
    },
    async restorePlan(plan) {
      this.loading = true
      try {
        await api.workoutApi.restoreWorkoutV2(plan.id)
        this.$toast.success(`Plan "${plan.title}" wurde wiederhergestellt.`)
        await this.loadPlans()
      } catch (error) {
        console.log(error)
        this.$toast.error("Der Plan konnte nicht wiederhergestellt werden.")
      } finally {
        this.loading = false
      }
    },
    copyPlan(plan) {
      this.$toast.info(`Plan "${plan.title}" wurde in die Zwischenablage kopiert.`)
      this.setPlanForCloning({ form: plan.originalObject })
      this.$router.push({ name: "WorkoutCreateNew", params: { isClone: "true" } })
    },
    async loadPlans() {
      if (!this.customer?._id) return
      this.loading = true
      try {
        const [newPlansResponse, oldPlansResponse] = await Promise.all([
          api.workoutApi.fetchCustomerWorkouts(this.customer._id).catch(() => []),
          api.getTrainingPlansForUser(this.customer._id).catch(() => []),
        ])

        // --- NEUE PLÄNE MAPPEN ---
        const mappedNew = (Array.isArray(newPlansResponse) ? newPlansResponse : []).map((plan) => {
          let displayTitle = plan.title
          if (plan.splits && Array.isArray(plan.splits.workouts) && plan.splits.workouts.length > 0) {
            displayTitle = plan.splits.workouts.map((workout) => workout.title).join(", ")
          }
          return {
            id: plan._id,
            title: displayTitle,
            createdDate: plan.createdAt,
            type: "new",
            deleted: plan.deleted,
            originalObject: plan,
            instructor: plan.instructor_id ? plan.instructor_id.vorname + " " + plan.instructor_id.nachname : plan.form?.instructor || "Unbekannt",
            allContributors: plan.allContributors || [],
            splits: plan.splits?.workouts || [],
            isExpanded: false,
          }
        })

        // --- ALTE PLÄNE MAPPEN ---
        const oldPlansArray = Array.isArray(oldPlansResponse) ? oldPlansResponse : []
        const mappedOld = oldPlansArray.map((plan) => {
          let instructorName = "Unbekannt"
          // HIER NEU: Wir greifen direkt auf das neue, eigenständige Feld zu
          if (plan.creatorInfo && plan.creatorInfo.vorname) {
            instructorName = `${plan.creatorInfo.vorname} ${plan.creatorInfo.nachname}`
          } else if (plan.form && plan.form.instructor) {
            // Fallback für alte Pläne, die das alte 'instructor' String-Feld nutzen
            instructorName = plan.form.instructor
          }

          return {
            id: plan._id,
            title: plan.form?.title || "Ohne Titel",
            createdDate: plan.form?.ts ? new Date(plan.form.ts * 1000).toISOString() : new Date().toISOString(),
            type: "old",
            instructor: instructorName,
            originalObject: plan,
          }
        })

        this.combinedPlans = [...mappedNew, ...mappedOld].sort((a, b) => new Date(b.createdDate) - new Date(a.createdDate))
      } catch (error) {
        console.error("Fehler beim Verarbeiten der Trainingspläne:", error)
        this.$toast.error("Fehler beim Verarbeiten der Pläne.")
      } finally {
        this.loading = false
      }
    },
    createNewPlan() {
      this.$router.push(`/workouts/createnew?userId=${this.customer._id}`)
    },
    createOldPlan() {
      return alert("Klassische Pläne können nur noch bis zum 05.10.2025 erstellt werden.")
      this.$router.push(`/workouts/create?userId=${this.customer._id}`)
    },
    editPlan(plan) {
      if (plan.type === "new") {
        this.setPlanForEditing({ form: plan.originalObject })
        this.$router.push({ name: "WorkoutCreateNew", params: { id: plan.id } })
      } else {
        this.editOldPlan(plan.originalObject)
      }
    },
    editOldPlan(plan) {
      for (let index in plan.exercises) {
        if (plan.exercises[index].exercise_id?._id) plan.exercises[index].exercise_id = plan.exercises[index].exercise_id._id
        let origExercise = this.exerciseList.find((ex) => ex._id === plan.exercises[index].exercise_id)
        if (origExercise) {
          if (origExercise.title) plan.exercises[index].title = origExercise.title
          if (origExercise.picture) plan.exercises[index].picture = origExercise.picture
          if (origExercise.manual) plan.exercises[index].manual = origExercise.manual
          if (origExercise.set_exercise_settings) plan.exercises[index].set_exercise_settings = origExercise.set_exercise_settings
          for (let setIndex in plan.exercises[index].sets) {
            plan.exercises[index].sets[setIndex].active = false
            plan.exercises[index].sets[setIndex].activeBefore = false
            origExercise.set_exercise_settings.forEach((exerciseSetting) => {
              if (!plan.exercises[index].sets[setIndex].settings.find((setting) => setting._id === exerciseSetting._id))
                plan.exercises[index].sets[setIndex].settings.push(exerciseSetting)
            })
          }
        } else {
          console.error("origExercise not found", plan.exercises[index].exercise_id)
        }
      }
      this.setEditInStore(JSON.parse(JSON.stringify(plan)))
      this.$router.push("/workouts/edit/" + plan._id)
    },
    printPlan(plan) {
      if (plan.type === "new") {
        this.selectedPlanForPrint = plan.originalObject
        this.printableSplits = plan.originalObject.splits.workouts
        this.showPrintSplitDialog = true
      } else {
        this.$toast.info("Druck für klassischen Plan wird vorbereitet...")
        this.downloadPDF(plan.originalObject).catch((err) => {
          this.$toast.error("Drucken des Plans fehlgeschlagen.")
          console.error(err)
        })
      }
    },
    async downloadPDF(plan) {
      this.printing = true
      await api.fetchPlanPDF(plan)
      this.printing = false
    },
    downloadSplit(index) {
      if (this.selectedPlanForPrint?._id) {
        this.$toast.info("Dein Download wird vorbereitet...")
        api.downloadSplitPDF(this.selectedPlanForPrint._id, index)
        this.showPrintSplitDialog = false
      }
    },
  },
  watch: {
    customer: {
      handler(newCustomer) {
        this.viewMode = "shortcuts"
        if (newCustomer?._id) {
          this.loadPlans()
          this.loadUpgrades() // <--- NEU HINZUGEFÜGT
        }
      },
      immediate: true,
    },
  },
}
</script>

<style scoped>
.customer-dashboard-wrapper {
  position: relative;
  padding: 20px;
  overflow: hidden;
}
.background-image {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 200px;
  background-size: cover;
  background-position: center;
  filter: blur(20px) brightness(0.8);
  transform: scale(1.2);
  z-index: 1;
}
.main-card {
  position: relative;
  z-index: 2;
  border-radius: 24px !important;
  margin-top: 80px;
  background: rgba(255, 255, 255, 0.85) !important;
  border: 1px solid rgba(255, 255, 255, 0.4);
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1) !important;
}
.avatar-container {
  position: absolute;
  top: 0;
  left: 50%;
  transform: translate(-50%, -50%);
}
.customer-avatar {
  border: 5px solid white;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.change-customer-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: rgba(255, 255, 255, 0.3);
  border-radius: 20px;
}
.action-card {
  border-radius: 16px !important;
  padding: 16px;
  cursor: pointer;
  transition: all 0.3s ease-in-out;
  border: 1px solid rgba(0, 0, 0, 0.08);
  height: 100%;
  text-align: center;
}
.action-card .card-content {
  display: flex;
  flex-direction: column;
  align-items: center;
}
.action-card .card-title {
  font-size: 1.1rem;
  font-weight: bold;
  color: #2c3e50;
}
.action-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 25px rgba(0, 0, 0, 0.08);
}
.new-plan-card {
  background: linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%);
  border-color: rgba(var(--v-primary-base-rgb), 0.3);
}
.old-plan-card {
  background: linear-gradient(135deg, #eceff1 0%, #ffffff 100%);
  border-color: rgba(0, 0, 0, 0.1);
}
.plan-list {
  padding: 0;
}
.plan-item-card {
  background: rgba(255, 255, 255, 0.7) !important;
  border: 1px solid rgba(0, 0, 0, 0.1) !important;
  border-radius: 16px !important;
  padding: 8px 16px;
  transition: all 0.2s ease;
}
.plan-item-card:hover {
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.07);
  transform: translateY(-2px);
  border-color: rgba(var(--v-primary-base-rgb), 0.5) !important;
}

/* Neue Split Listen Styles */
.splits-container {
  background-color: rgba(255, 255, 255, 0.5);
  border-radius: 8px;
  padding: 8px 12px;
}
.split-item {
  border-left: 3px solid var(--v-primary-base);
  padding-left: 12px;
  margin-bottom: 8px;
}
.split-item:last-child {
  margin-bottom: 0;
}
.split-header {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}
.split-creator-badge {
  height: 22px;
  font-size: 0.75rem;
  padding: 0 8px;
}

.empty-state {
  padding: 32px 0;
}
.view-swap-enter-active,
.view-swap-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}
.view-swap-enter {
  opacity: 0;
  transform: translateY(20px);
}
.view-swap-leave-to {
  opacity: 0;
  transform: translateY(-20px);
}
.delete-dialog-card {
  background-color: transparent !important;
  box-shadow: none !important;
  overflow: visible !important;
}
.delete-dialog-card .v-card {
  border-radius: 24px !important;
  background: #1e1e1e;
  border: 1px solid rgba(255, 82, 82, 0.3);
  position: relative;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(255, 82, 82, 0.2);
}
.delete-dialog-bg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: radial-gradient(circle at 50% 0%, rgba(255, 82, 82, 0.15), transparent 70%);
  pointer-events: none;
}
.delete-icon {
  animation: alert-icon-pop 0.8s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;
}
@keyframes alert-icon-pop {
  0% {
    transform: scale(0.6) rotate(-15deg);
    opacity: 0;
  }
  60% {
    transform: scale(1.1) rotate(10deg);
    opacity: 1;
  }
  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.interactive-card {
  border-radius: 16px !important;
  cursor: pointer;
  transition: all 0.2s ease-in-out;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.interactive-card:hover {
  transform: translateY(-4px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.08) !important;
  border-color: rgba(var(--v-primary-base-rgb), 0.5) !important;
}
.card-action-title {
  font-size: 1.1rem;
  font-weight: 700;
  color: #333;
}
.card-status-text {
  font-size: 0.85rem;
  color: #616161;
  display: flex;
  align-items: center;
}
.info-card {
  border-radius: 16px !important;
  border: 1px solid rgba(0, 0, 0, 0.08) !important;
}
.info-card-title {
  font-size: 0.9rem !important;
  font-weight: 600;
  color: #555;
  padding-bottom: 8px;
  border-bottom: 1px solid #eee;
}
</style>
